import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import moment, {Moment} from 'moment';
import {Observable, of} from 'rxjs';
import {map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../../components/destroyable';
import {SecurityService} from '../../../components/security';
import {isSmartSecurityKey, SmartSecurityService} from '../../../components/smart-security';
import {
    Bar,
    LibrarySymbolInfo,
    ResolutionString
} from '../../../components/trading-view-chart/charting-library/datafeed-api';
import {GetBarsResult, HistoryProvider} from '../../../components/trading-view-chart/datafeed/history-provider';
import {CANDLE_PERIOD_MOMENT_DURATION_DICT, CandlePeriod} from '../../../enums/candle-period';
import {Candle} from '../../../models/candle';
import {Security} from '../../../models/security';
import {parseSecurityKey} from '../../../models/security-key';
import {CandleService} from './candle.service';
import {CHART_RESOLUTIONS_DICT} from './chart-resolution';

/**
 * Every ChartWidget must have own ChartHistoryProvider,
 * because of OnDestroy support and lastProvidedBarTime value calculating.
 */
@Injectable()
export class ChartHistoryProvider implements HistoryProvider {

    public static convertCandleToBar(candle: Candle): Bar {

        // [COMMON, TRAD-135] In Chart widget show open candle time instead of close time
        const SHOW_CANDLE_OPEN_TIME_INSTEAD_OF_CLOSE = true;

        const mTime = moment.utc(candle.closeTime);

        if (SHOW_CANDLE_OPEN_TIME_INSTEAD_OF_CLOSE) {
            const candleDuration = CANDLE_PERIOD_MOMENT_DURATION_DICT[candle.candlePeriod];
            mTime.subtract(candleDuration);
        }

        return {
            time:   mTime.valueOf(),
            open:   candle.openPrice,
            close:  candle.closePrice,
            high:   candle.highPrice,
            low:    candle.lowPrice,
            volume: candle.volume
        };
    }

    public lastProvidedBarTime: number;

    public readonly destroy$ = destroy$();

    constructor(
        private candleService: CandleService,

        // [HED-930] It's not SmartSecurityService here, since it requires accounts
        private securityService: SecurityService
    ) {}

    public getBars(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        unixRangeStartDate: number,
        unixRangeEndDate: number
    ): Promise<GetBarsResult> {
        return this.getBarsStream(
            symbolInfo,
            resolution,
            unixRangeStartDate,
            unixRangeEndDate
        ).pipe(
            takeUntil(this.destroy$)
        ).toPromise();
    }

    public getBarsStream(
        symbolInfo: LibrarySymbolInfo,
        resolution: ResolutionString,
        unixRangeStartDate: number,
        unixRangeEndDate: number
    ): Observable<GetBarsResult> {
        const securityKey = parseSecurityKey(symbolInfo.ticker);
        const candlePeriod = CHART_RESOLUTIONS_DICT[resolution].candlePeriod;
        const dateFrom = moment.unix(unixRangeStartDate).toDate();
        const dateTo = this.getBarsRequestDateToPulledToNow(moment.unix(unixRangeEndDate), candlePeriod);

        // [TT-247] Handle Smart security (workaround)
        const security$: Observable<Security> = isSmartSecurityKey(securityKey)
            ? (this.securityService as SmartSecurityService).resolveSmartSecurity(securityKey).pipe(
                map(securities => securities && securities[0])
            )
            : this.securityService.getSecurityBySecurityKey(securityKey);

        return security$.pipe(
            switchMap(security => security
                ? this.candleService.getCandlesByParameters({securityKey: security, candlePeriod, dateFrom, dateTo})
                : of([])
            ),
            map(candles => candles.map(ChartHistoryProvider.convertCandleToBar)),
            tap(bars => this.lastProvidedBarTime = !bars.length ? 0 : _.last(bars).time),
            map<Bar[], GetBarsResult>(bars => ({
                bars,
                meta: {noData: !bars.length}
            }))
        );
    }

    /**
     * Omits range's end date in case if it's quite near to the local current time.
     * This approach should decrease effect from local time lag.
     * @param {Moment} timeRangeEndMoment
     * @param {CandlePeriod} candlePeriod
     * @returns {Date | null}
     */
    private getBarsRequestDateToPulledToNow(timeRangeEndMoment: Moment, candlePeriod: CandlePeriod): Date | null {
        const BEFORE_NOW_THRESHOLD_PERIOD_TIMES = 10;
        const MIN_MS_BEFORE_NOW_THRESHOLD = 1000 * 60 * BEFORE_NOW_THRESHOLD_PERIOD_TIMES;

        let dateTo: Moment | null = timeRangeEndMoment ? timeRangeEndMoment : null;

        if (dateTo) {
            const candlePeriodMs = CANDLE_PERIOD_MOMENT_DURATION_DICT[candlePeriod].milliseconds();
            const msBeforeNowThreshold = Math.max(
                candlePeriodMs * BEFORE_NOW_THRESHOLD_PERIOD_TIMES,
                MIN_MS_BEFORE_NOW_THRESHOLD
            );
            const msBeforeNow = Date.now() - dateTo.valueOf();

            dateTo = msBeforeNow <= msBeforeNowThreshold ? null : dateTo;
        }

        return dateTo && dateTo.toDate();
    }

}
